<script setup lang="ts">
import FinestCard from '@/components/FinestCard.vue'
import FinestFieldGrid from '@/components/finest-field-grid/FinestFieldGrid.vue'
import type {
  InlineEditableCommitPayload,
  InlineEditableField,
  InlineEditableModel,
  InlineEditableValue,
} from '@/components/finest-field-grid/types'
import PropertyRentalUnitsCard from '@/components/portfolio/PropertyRentalUnitsCard.vue'
import { useSyncedModel } from '@/composables/useSyncedModel'
import { hasBusinessSpace, hasLivingSpace } from '@/pages/investors/portfolio/objectType'
import { useEstateObjectUpdate } from '@/pages/investors/portfolio/useEstateObjectUpdate'
import type { FieldStateMap } from '@/utils/field-state'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import EstateObjectData = App.Data.EstateObject.EstateObjectData
import RentalUnitData = App.Data.EstateObject.RentalUnitData

const props = defineProps<{
  fieldStates: FieldStateMap
  estateObject: EstateObjectData
  rentalUnits: RentalUnitData[]
}>()

const { t } = useI18n()

const totalSpace = computed(
  () => (props.estateObject.living_space_size ?? 0) + (props.estateObject.business_space_size ?? 0),
)

const spaceField = (key: string): InlineEditableField => ({
  key,
  label: t(`fields.${key}`),
  type: 'number',
  suffix: ' m²',
})

const spaceFields = computed<InlineEditableField[]>(() => {
  const type = props.estateObject.estate_object_type

  return [
    spaceField('property_size'),
    ...(hasLivingSpace(type) ? [spaceField('living_space_size')] : []),
    ...(hasBusinessSpace(type) ? [spaceField('business_space_size')] : []),
    { ...spaceField('total_space'), readonly: true },
  ]
})

const editableKeys = ['property_size', 'living_space_size', 'business_space_size']

const seedModel = (): InlineEditableModel => {
  const source = props.estateObject as unknown as Record<string, InlineEditableValue>

  return {
    ...Object.fromEntries(editableKeys.map((key) => [key, source[key] ?? null])),
    total_space: totalSpace.value,
  }
}

const { model, confirm } = useSyncedModel(seedModel)

const update = useEstateObjectUpdate(props.estateObject.id ?? '', confirm)

const saveSpace = ({ key, value }: InlineEditableCommitPayload) => update({ [key]: value })
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="grid items-start gap-4 lg:grid-cols-2">
      <FinestCard :title="t('cards.space')" icon="pi pi-th-large">
        <FinestFieldGrid v-model="model" :fields="spaceFields" :field-states="fieldStates" @commit="saveSpace" />
      </FinestCard>
    </div>

    <PropertyRentalUnitsCard :estate-object="estateObject" :rental-units="rentalUnits" :field-states="fieldStates" />
  </div>
</template>

<i18n>
{
  "de": {
    "cards": {
      "space": "Flächen"
    },
    "fields": {
      "property_size": "Grundstücksgröße",
      "living_space_size": "Wohnfläche",
      "business_space_size": "Gewerbefläche",
      "total_space": "Gesamtfläche"
    }
  },
  "en": {
    "cards": {
      "space": "Areas"
    },
    "fields": {
      "property_size": "Plot size",
      "living_space_size": "Living space",
      "business_space_size": "Commercial space",
      "total_space": "Total area"
    }
  }
}
</i18n>
